<template>
  <div
    id="prices"
    class="price-section d-flex flex-column pb-10 ga-6"
  >
    <PriceHeader
      :class="{ 'my-n3': isSpecialistsOnly }"
      :lpu="context.lpu"
      :town-name="context.town.name"
      :currency-abbreviation="context.currency.symbol"
      :hide-title-and-phone="isSpecialistsOnly"
      :appointment-phone="appointmentPhone"
      :price-range="context.price_range"
      :price-range-doctors="context.price_range_doctors"
      :price-range-mrt="context.price_range_mrt"
      :price-range-kt="context.price_range_kt"
      :price-range-uzi="context.price_range_uzi"
      :price-range-rentgen="context.price_range_rentgen"
      :price-range-uslugi="context.price_range_uslugi"
    />
    <PriceStaffLinks
      v-if="context.request.user.is_staff"
      :lpu-site="context.lpu.site || undefined"
    />
    <template v-if="hasMedflexApi">
      <div class="d-flex flex-column ga-3">
        <div class="d-flex align-center justify-space-between">
          <PriceOnlineSwitch
            v-if="hasOnlineSwitch"
            :value="isOnlineOnly"
            @input="setOnlineOnly"
          />
          <PriceUpdateDate :data="context.unloaded_price_list_data" />
        </div>
        <PriceSearch
          :items="serviceCategories"
          @search:text="searchByText"
          @search:class="searchByClass"
          @search:default="resetSearch"
        />
      </div>
    </template>
    <PriceUpdateDate
      v-else
      :data="context.unloaded_price_list_data"
    />
    <div
      v-if="!isSpecialistsOnly"
      class="price-section__loader my-n3"
      data-qa="lpu_page_price_loading"
    >
      <v-progress-linear
        v-show="isLoading"
        indeterminate
        height="2"
        color="var(--lpu-branding-color-primary)"
        class="price-section__loader-bar"
      />
    </div>
    <div class="price-section__columns d-flex align-start ga-8">
      <PriceCategoryNav
        v-if="categoryNavItems.length"
        :items="categoryNavItems"
        :active-name="selectedCategoryName || undefined"
        @select="searchByClass"
        @select:default="resetSearch"
      />
      <PriceCatalog
        :has-medflex-ui="hasMedflexUi"
        :has-medflex-api="hasMedflexApi"
        :show-pagination="showPagination"
        :show-all-button="showAllButton"
        :is-not-found="isNotFound"
        :is-enabled-direct="Boolean(context.lpu.appointment_service_direct)"
        :hide-group-titles="hideGroupTitles"
        :service-classes="displayClasses"
        :specialities="specialities"
        :categories="categories"
        :lpu="context.lpu"
        :lpu-id="context.lpu.id"
        :town-translit="context.lpu.town.translit"
        :town-timedelta="townTimedelta"
        :empty-src="emptySrc"
        :current-page="currentPage"
        :page-count="pageCount"
        @show-all="showAllServices"
        @page-input="relocatePage"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import usePriceSection from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/composables/usePriceSection'
import PriceHeader from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceHeader/PriceHeader.vue'
import PriceStaffLinks from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceStaffLinks.vue'
import PriceOnlineSwitch from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceToolbar/PriceOnlineSwitch.vue'
import PriceUpdateDate from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceToolbar/PriceUpdateDate.vue'
import PriceSearch from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceToolbar/PriceSearch.vue'
import PriceCategoryNav from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceCategoryNav.vue'
import PriceCatalog from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceCatalog/PriceCatalog.vue'

const {
  context,
  appointmentPhone,
  serviceCategories,
  pageCount,
  currentPage,
  isNotFound,
  isLoading,
  isOnlineOnly,
  selectedCategoryName,
  hasOnlineSwitch,
  showPagination,
  showAllButton,
  searchByText,
  searchByClass,
  resetSearch,
  relocatePage,
  setOnlineOnly,
  showAllServices,
  specialities,
  categories,
  hasMedflexApi,
  displayClasses,
  hasMedflexUi,
  isSpecialistsOnly,
  categoryNavItems,
  hideGroupTitles,
  emptySrc,
  townTimedelta,
} = usePriceSection()
</script>

<style scoped lang="scss">
.price-section {
  // min-w-0 в проекте нет как utility — только через CSS.
  // Иначе DayTabs/Swiper раздувает flex-ряд за container.
  min-width: 0;
  max-width: 100%;
}

.price-section__columns {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.price-section__loader {
  height: 2px;
}

.price-section__loader-bar {
  ::v-deep .v-progress-linear__indeterminate {
    .long,
    .short {
      background-color: var(--lpu-branding-color-primary) !important;
    }
  }
}
</style>
